/**
 * Inspection rounds as the platform sends them (routers/work.py,
 * work/rounds.py), and the round as it is sent.
 *
 * Read leniently where a field is description, strictly where it decides
 * something: ids, the result of an answer and the work's number are required,
 * because a round read back with an answer it cannot place would draw a topic
 * as inspected that nobody looked at.
 */
import { z } from "zod";
import type {
  InspectionRound,
  InspectionRoundInput,
  InspectionRoundResult,
  RoundAnswer,
  RoundWork,
} from "../domain/inspectionRounds";

const str = z.string().nullish();
const num = z.union([z.number(), z.string().transform(Number)]).nullish();
const attrs = z
  .record(z.string(), z.unknown())
  .nullish()
  .transform((v) => v ?? {});
const u = <T>(v: T | null | undefined): T | undefined => (v == null ? undefined : v);

const wireWork = z
  .object({
    id: z.string(),
    number: z.string(),
    status: z.string(),
    priority: num,
    observation_id: str,
    observation: str,
    item_key: str,
  })
  .passthrough();

export const wireRoundResult = z
  .object({
    id: z.string(),
    reference: z.string(),
    status: z.string(),
    created: z.boolean().nullish(),
    replayed: z.boolean().nullish(),
    completed_at: str,
    score: num,
    answers: z.number().nullish(),
    failed: z.number().nullish(),
    skipped: z.number().nullish(),
    missing_evidence: z.array(z.string()).nullish(),
    warnings: z.array(z.string()).nullish(),
    work_orders: z.array(wireWork).nullish(),
  })
  .passthrough();

const wireAnswer = z
  .object({
    id: z.string(),
    client_id: str,
    item_key: z.string(),
    result: str,
    value_text: str,
    notes: str,
    skipped_reason: str,
    attrs,
    defect_id: str,
    photos: z.number().nullish(),
  })
  .passthrough();

const wireRound = z
  .object({
    id: z.string(),
    reference: str,
    client_id: str,
    template: z.string(),
    template_name: str,
    status: z.string(),
    started_at: str,
    completed_at: str,
    score: num,
    captured_offline: z.boolean().nullish(),
    inspector: str,
    attrs,
    answers: z.array(wireAnswer).default([]),
    work_orders: z.array(wireWork).default([]),
  })
  .passthrough();

export const wireRounds = z.object({ rounds: z.array(wireRound) }).passthrough();

const work = (w: z.infer<typeof wireWork>): RoundWork => ({
  id: w.id,
  number: w.number,
  status: w.status,
  priority: u(w.priority),
  observationId: u(w.observation_id),
  observation: u(w.observation),
  itemKey: u(w.item_key),
});

export function toWireRound(input: InspectionRoundInput): Record<string, unknown> {
  return {
    site_id: input.siteId,
    template: input.template,
    client_id: input.clientId,
    attrs: input.attrs ?? {},
    observations: input.answers.map((a) => ({
      item_key: a.itemKey,
      value: a.value,
      notes: a.notes ?? null,
      skipped_reason: a.skippedReason ?? null,
      client_id: a.clientId,
      attrs: a.attrs ?? {},
      ...(a.lon != null && a.lat != null ? { lon: a.lon, lat: a.lat } : {}),
    })),
    complete: input.complete,
    allow_missing_evidence: input.allowMissingEvidence,
    work_orders: input.work.map((w) => ({
      observation: w.observation,
      title: w.title,
      description: w.description ?? null,
      priority: w.priority,
      sla_hours: w.slaHours ?? null,
      area: w.area ?? null,
      trade: w.trade ?? null,
    })),
  };
}

export function fromWireRoundResult(r: z.infer<typeof wireRoundResult>): InspectionRoundResult {
  return {
    id: r.id,
    reference: r.reference,
    status: r.status,
    created: r.created ?? false,
    replayed: r.replayed ?? false,
    completedAt: u(r.completed_at),
    score: u(r.score),
    answers: r.answers ?? 0,
    failed: r.failed ?? 0,
    skipped: r.skipped ?? 0,
    missingEvidence: r.missing_evidence ?? [],
    warnings: r.warnings ?? [],
    workOrders: (r.work_orders ?? []).map(work),
  };
}

const answer = (a: z.infer<typeof wireAnswer>): RoundAnswer => ({
  id: a.id,
  clientId: u(a.client_id),
  itemKey: a.item_key,
  result: u(a.result),
  valueText: u(a.value_text),
  notes: u(a.notes),
  skippedReason: u(a.skipped_reason),
  attrs: a.attrs,
  defectId: u(a.defect_id),
  photos: a.photos ?? 0,
});

export function fromWireRounds(raw: z.infer<typeof wireRounds>): InspectionRound[] {
  return raw.rounds.map((r) => ({
    id: r.id,
    reference: r.reference ?? r.id.slice(0, 8).toUpperCase(),
    clientId: u(r.client_id),
    template: r.template,
    templateName: r.template_name ?? r.template,
    status: r.status,
    startedAt: u(r.started_at),
    completedAt: u(r.completed_at),
    score: u(r.score),
    inspector: u(r.inspector),
    capturedOffline: u(r.captured_offline),
    attrs: r.attrs,
    answers: r.answers.map(answer),
    workOrders: r.work_orders.map(work),
  }));
}
